import React, { useState, useEffect, useRef, useCallback } from "react";
import { Drawer, Button, Space, message, Alert, Modal, Checkbox, Tag } from "antd";
import { ExclamationCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { scheduleService } from "../../../../api/enhancedApi";
import FlightCompare from "../../ScheduleEditor/components/FlightCompare";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/**
 * 计划发布确认抽屉组件
 * @param {Object} props
 * @param {boolean} props.visible - 是否显示抽屉
 * @param {function} props.onClose - 关闭抽屉回调
 * @param {Object} props.schedule - 计划对象
 * @param {function} props.onPublish - 发布成功后的回调
 */
const SchedulePublishDrawer = ({ visible, onClose, schedule, onPublish }) => {
    const [loading, setLoading] = useState(false);
    const [publishLoading, setPublishLoading] = useState(false);
    const [compareResult, setCompareResult] = useState(null);
    const [manualMatchings, setManualMatchings] = useState([]);
    const compareRef = useRef(null);

    // 加载计划参数
    const loadScheduleParameters = useCallback(async () => {
        if (!schedule?.id) return;

        try {
            setLoading(true);
            // 加载计划详情，里面已经包含了 parameter 信息
            const detailResponse = await scheduleService.getScheduleDetail(schedule.id, { limit: 1 }); // 因为明细只需要加载一条，这里我们用到的只有 parameter 信息
            const scheduleDetail = detailResponse?.data; // 获取响应数据

            // 直接从 scheduleDetail 中获取 parameter_id 和 parameter 对象
            const parameterData = scheduleDetail?.parameter; // 直接使用已加载的 parameter 对象

            let savedMatchings = [];
            let savedCompareResult = null;

            // 检查并提取已保存的匹配数据，直接从 detailResponse 的数据中获取
            if (parameterData?.parameter_json?.flight_compare_result) {
                const savedData = parameterData.parameter_json.flight_compare_result;

                // 提取手动匹配数据
                if (savedData.manualMatchings) {
                    savedMatchings = savedData.manualMatchings;
                    setManualMatchings(savedMatchings); // 更新状态
                }

                // 如果有保存的对比详情，也恢复对比结果状态
                if (savedData.comparisonDetails) {
                    savedCompareResult = savedData.comparisonDetails;
                    setCompareResult(savedCompareResult);
                    console.log("已恢复确认阶段保存的对比结果:", savedData);
                }
            }

            // 无论是否找到匹配，都尝试加载对比组件
            // 如果有保存的匹配，则使用它们加载；否则，不带参数加载
            if (compareRef.current && visible) {
                compareRef.current.loadCurrentOperations(savedMatchings);
            }
        } catch (error) {
            console.error("加载计划详情或处理参数时失败:", error);
            // 加载失败时仍然尝试正常加载对比
            if (compareRef.current && visible) {
                compareRef.current.loadCurrentOperations();
            }
        } finally {
            setLoading(false);
        }
    }, [schedule?.id, visible]);

    // 当抽屉打开时加载参数和对比数据
    useEffect(() => {
        if (visible && schedule?.id) {
            loadScheduleParameters();
        }
    }, [visible, schedule?.id, loadScheduleParameters]);

    // 处理手动匹配变更
    const handleMatchingsChange = (matchings) => {
        setManualMatchings(matchings);
    };

    // 处理对比结果变更
    const handleCompareResult = (result) => {
        setCompareResult(result);
    };

    const buildComparisonDetails = () => {
        const comparisonDetails = {
            addedFlights: compareResult.addedFlights || [],
            removedOperations: compareResult.removedOperations || [],
            changedPairs: compareResult.changedPairs || [],
            manualMatchings: [],
        };

        if (manualMatchings && manualMatchings.length > 0) {
            manualMatchings.forEach((matching) => {
                const { leftIds, rightKeys, type } = matching;
                comparisonDetails.manualMatchings.push({
                    replacement_type: type,
                    replaced_operation_ids: leftIds || [],
                    replaces_draft_keys: rightKeys || [],
                    manual: true,
                });
            });
        }

        return comparisonDetails;
    };

    // 确认发布（支持 forceOptions 分项强制）
    const confirmPublish = async (forceOptions = null) => {
        if (!schedule?.id) return;

        try {
            setPublishLoading(true);
            const isForced = forceOptions !== null;
            message.loading(isForced ? "正在强制发布计划..." : "正在发布计划...", 0);

            if (!compareResult) {
                message.destroy();
                message.warning("请先完成对比分析");
                setPublishLoading(false);
                return;
            }

            const comparisonDetails = buildComparisonDetails();

            const payload = { comparisonDetails };
            if (forceOptions) {
                payload.forceOptions = forceOptions;
            }

            await scheduleService.publishToOperations(schedule.id, payload);

            message.destroy();
            message.success(isForced ? "计划已强制发布成功" : "计划发布成功");
            onPublish && onPublish();
            onClose();
        } catch (error) {
            message.destroy();

            const errorDetail = error.response?.data?.detail;
            if (errorDetail?.error_type === "PUBLISH_VALIDATION_ERROR") {
                showValidationErrorModal(errorDetail);
            } else if (errorDetail?.error_type === "CONTINUITY_VALIDATION_ERROR") {
                // 向后兼容：旧后端仍返回旧 error_type 时的兜底
                showValidationErrorModal({
                    ...errorDetail,
                    issues: { continuity: errorDetail.continuity_issues || [] },
                    can_force_options: { continuity: true },
                });
            } else {
                message.error(`发布失败: ${error.message || "未知错误"}`);
                console.error("发布计划失败:", error);
            }
        } finally {
            setPublishLoading(false);
        }
    };

    // 展示发布校验问题确认对话框（合并连续性 + 遗留航班 + 同机时间冲突）
    const showValidationErrorModal = (errorDetail) => {
        const issues = errorDetail.issues || {};
        const canForce = errorDetail.can_force_options || {};
        const continuityIssues = issues.continuity || [];
        const orphanCandidates = issues.orphan || [];
        const timeConflictIssues = issues.timeConflict || [];
        const aircraftAvailabilityIssues = issues.aircraftAvailability || [];
        const hasBlockingAircraftAvailability = aircraftAvailabilityIssues.length > 0;
        const hasAnyForceOption = !hasBlockingAircraftAvailability && Boolean(
            canForce.continuity || canForce.orphan || canForce.timeConflict,
        );

        let forceSelections = {
            continuity: hasAnyForceOption && !!canForce.continuity,
            orphan: hasAnyForceOption && !!canForce.orphan,
            timeConflict: hasAnyForceOption && !!canForce.timeConflict,
        };

        const formatFlightDate = (isoStr) => {
            if (!isoStr) return "-";
            return `${dayjs.utc(isoStr).format("M/D HH:mm")} UTC`;
        };

        const statusColorMap = {
            SCHEDULED: "blue",
            ACTIVE: "green",
            DEPARTED: "cyan",
            COMPLETED: "default",
            DELAYED: "orange",
            DIVERTED: "purple",
        };

        Modal.confirm({
            title: "发布校验问题",
            icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
            width: 700,
            content: (
                <div style={{ maxHeight: 500, overflow: "auto" }}>
                    {/* 连续性问题区域 */}
                    {continuityIssues.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <Alert
                                type="warning"
                                message={`航班连续性问题（${continuityIssues.length} 个）`}
                                description="发布此计划将导致航班链出现航线断裂。"
                                style={{ marginBottom: 12 }}
                            />
                            {continuityIssues.map((issue, index) => (
                                <div
                                    key={`cont-${index}`}
                                    style={{
                                        background: "#fffbe6",
                                        border: "1px solid #ffe58f",
                                        borderRadius: 4,
                                        padding: 12,
                                        marginBottom: 8,
                                    }}
                                >
                                    <div style={{ fontWeight: 500, marginBottom: 8 }}>
                                        问题 {index + 1}: 航线断裂
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                        <span style={{ background: "#fff", padding: "4px 8px", borderRadius: 4, border: "1px solid #d9d9d9" }}>
                                            {issue.flight_before?.flight_number} ({issue.flight_before?.route})
                                            <span style={{ color: "#888", marginLeft: 4, fontSize: 12 }}>
                                                STA: {formatFlightDate(issue.flight_before?.sta)}
                                            </span>
                                        </span>
                                        <span style={{ color: "#ff4d4f", fontWeight: 600 }}>→ 断裂 →</span>
                                        <span style={{ background: "#fff", padding: "4px 8px", borderRadius: 4, border: "1px solid #d9d9d9" }}>
                                            {issue.flight_after?.flight_number} ({issue.flight_after?.route})
                                            <span style={{ color: "#888", marginLeft: 4, fontSize: 12 }}>
                                                STD: {formatFlightDate(issue.flight_after?.std)}
                                            </span>
                                        </span>
                                    </div>
                                    <div style={{ marginTop: 8, color: "#666", fontSize: 13 }}>
                                        {issue.description}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 遗留航班问题区域 */}
                    {orphanCandidates.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <Alert
                                type="error"
                                message={`遗留航班冲突（${orphanCandidates.length} 个）`}
                                description="发现已有航班与本次新建航班重复，可能产生重复航班记录。"
                                icon={<WarningOutlined />}
                                style={{ marginBottom: 12 }}
                            />
                            <div style={{ border: "1px solid #f0f0f0", borderRadius: 4, overflow: "hidden" }}>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        background: "#fafafa",
                                        padding: "8px 12px",
                                        fontWeight: 500,
                                        fontSize: 13,
                                        borderBottom: "1px solid #f0f0f0",
                                    }}
                                >
                                    <span style={{ color: "#cf1322" }}>已有航班</span>
                                    <span style={{ color: "#1677ff" }}>本次新建</span>
                                </div>
                                {orphanCandidates.map((candidate, index) => (
                                    <div
                                        key={`orphan-${index}`}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            padding: "10px 12px",
                                            borderBottom: index < orphanCandidates.length - 1 ? "1px solid #f0f0f0" : "none",
                                            fontSize: 13,
                                        }}
                                    >
                                        <div style={{ color: "#cf1322" }}>
                                            <div style={{ fontWeight: 500 }}>
                                                {candidate.existing.flight_number} {candidate.existing.route}
                                            </div>
                                            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                                                STD: {formatFlightDate(candidate.existing.std)}
                                                <Tag
                                                    color={statusColorMap[candidate.existing.status] || "default"}
                                                    style={{ marginLeft: 6, fontSize: 11 }}
                                                >
                                                    {candidate.existing.status}
                                                </Tag>
                                            </div>
                                        </div>
                                        <div style={{ color: "#1677ff" }}>
                                            <div style={{ fontWeight: 500 }}>
                                                {candidate.new.flight_number} {candidate.new.route}
                                            </div>
                                            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                                                STD: {formatFlightDate(candidate.new.std)}
                                                <Tag color="blue" style={{ marginLeft: 6, fontSize: 11 }}>
                                                    {candidate.new.status}
                                                </Tag>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 同机时间冲突区域 */}
                    {timeConflictIssues.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <Alert
                                type="error"
                                message={`同机时间冲突（${timeConflictIssues.length} 个）`}
                                description="同一飞机的前序航班有效到达时间晚于后续航班有效起飞时间。"
                                icon={<WarningOutlined />}
                                style={{ marginBottom: 12 }}
                            />
                            <div style={{ border: "1px solid #f0f0f0", borderRadius: 4, overflow: "hidden" }}>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        background: "#fafafa",
                                        padding: "8px 12px",
                                        fontWeight: 500,
                                        fontSize: 13,
                                        borderBottom: "1px solid #f0f0f0",
                                    }}
                                >
                                    <span style={{ color: "#cf1322" }}>前序航班</span>
                                    <span style={{ color: "#1677ff" }}>后续航班</span>
                                </div>
                                {timeConflictIssues.map((issue, index) => {
                                    const before = issue.flight_before || {};
                                    const after = issue.flight_after || {};
                                    return (
                                        <div
                                            key={`time-conflict-${index}`}
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "1fr 1fr",
                                                padding: "10px 12px",
                                                borderBottom: index < timeConflictIssues.length - 1 ? "1px solid #f0f0f0" : "none",
                                                fontSize: 13,
                                            }}
                                        >
                                            <div style={{ color: "#cf1322" }}>
                                                <div style={{ fontWeight: 500 }}>
                                                    {before.flight_number} {before.route}
                                                </div>
                                                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                                                    到达: {formatFlightDate(before.effective_arr)}
                                                    <Tag color="red" style={{ marginLeft: 6, fontSize: 11 }}>
                                                        {issue.aircraft_reg || "未知机号"}
                                                    </Tag>
                                                </div>
                                            </div>
                                            <div style={{ color: "#1677ff" }}>
                                                <div style={{ fontWeight: 500 }}>
                                                    {after.flight_number} {after.route}
                                                </div>
                                                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                                                    起飞: {formatFlightDate(after.effective_dep)}
                                                    {typeof issue.overlap_minutes === "number" && (
                                                        <Tag color="volcano" style={{ marginLeft: 6, fontSize: 11 }}>
                                                            重叠 {issue.overlap_minutes} 分钟
                                                        </Tag>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {aircraftAvailabilityIssues.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <Alert
                                type="error"
                                message={`飞机不可用冲突（${aircraftAvailabilityIssues.length} 个）`}
                                description="该问题不能强制发布。请返回计划编辑器调整航班时间、执行飞机或飞机可用性主数据。"
                                icon={<WarningOutlined />}
                                style={{ marginBottom: 12 }}
                            />
                            {aircraftAvailabilityIssues.map((issue, index) => (
                                <div
                                    key={`aircraft-availability-${issue.window_id || index}`}
                                    style={{
                                        background: "#fff2f0",
                                        border: "1px solid #ffccc7",
                                        borderRadius: 4,
                                        padding: 12,
                                        marginBottom: 8,
                                    }}
                                >
                                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                        {issue.flight_number || `航班 ${issue.flight_id || "-"}`}
                                        <Tag color="red" style={{ marginLeft: 8 }}>
                                            {issue.aircraft_reg || "未知机号"}
                                        </Tag>
                                    </div>
                                    <div>
                                        {issue.departure_airport || "?"} → {issue.arrival_airport || "?"}：
                                        {formatFlightDate(issue.flight_start)} - {formatFlightDate(issue.flight_end)}
                                    </div>
                                    <div style={{ marginTop: 4 }}>
                                        不可用窗口：{issue.window_title || issue.window_type || "未命名窗口"}
                                        {issue.airport_code ? ` @ ${issue.airport_code}` : ""}
                                        ，{formatFlightDate(issue.window_start)} - {issue.window_end ? formatFlightDate(issue.window_end) : "无限期"}
                                    </div>
                                    <div style={{ marginTop: 4, color: "#cf1322" }}>
                                        {issue.message}
                                    </div>
                                    {issue.next_action && (
                                        <div style={{ marginTop: 4, color: "#666" }}>
                                            建议：{issue.next_action}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 强制发布勾选项 */}
                    {hasAnyForceOption && (
                        <div
                            style={{
                                background: "#f6f6f6",
                                borderRadius: 4,
                                padding: 12,
                                marginTop: 8,
                            }}
                        >
                            <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>强制发布选项：</div>
                            {canForce.continuity && (
                            <Checkbox
                                defaultChecked={true}
                                onChange={(e) => {
                                    forceSelections.continuity = e.target.checked;
                                }}
                            >
                                忽略连续性问题
                            </Checkbox>
                            )}
                            {canForce.orphan && (
                            <Checkbox
                                defaultChecked={true}
                                onChange={(e) => {
                                    forceSelections.orphan = e.target.checked;
                                }}
                                style={{ marginLeft: canForce.continuity ? 16 : 0 }}
                            >
                                忽略遗留航班告警
                            </Checkbox>
                            )}
                            {canForce.timeConflict && (
                            <Checkbox
                                defaultChecked={true}
                                onChange={(e) => {
                                    forceSelections.timeConflict = e.target.checked;
                                }}
                                style={{ marginLeft: canForce.continuity || canForce.orphan ? 16 : 0 }}
                            >
                                忽略时间冲突告警
                            </Checkbox>
                            )}
                        </div>
                    )}
                </div>
            ),
            okText: hasAnyForceOption ? "强制发布" : "返回修改",
            okButtonProps: { danger: hasAnyForceOption },
            cancelText: "取消",
            cancelButtonProps: hasAnyForceOption ? {} : { style: { display: "none" } },
            onOk: () => {
                if (!hasAnyForceOption) {
                    return Promise.resolve();
                }
                const hasAnyForce = forceSelections.continuity || forceSelections.orphan || forceSelections.timeConflict;
                if (!hasAnyForce) {
                    message.warning("请至少勾选一项强制发布选项");
                    return Promise.reject();
                }
                confirmPublish({
                    continuity: forceSelections.continuity || false,
                    orphan: forceSelections.orphan || false,
                    timeConflict: forceSelections.timeConflict || false,
                });
            },
        });
    };

    return (
        <Drawer
            title={`发布确认: ${schedule?.name || "未命名计划"}`}
            placement="right"
            width="95%"
            onClose={onClose}
            open={visible}
            extra={
                <Space>
                    <Button onClick={onClose}>取消</Button>
                    <Button type="primary" loading={publishLoading} onClick={() => confirmPublish(null)} disabled={!compareResult}>
                        确认发布
                    </Button>
                </Space>
            }
            destroyOnHidden={true}
        >
            <div className="publish-drawer-content">
                <div className="publish-instructions">
                    <Alert
                        message="发布前请确认"
                        description={
                            <div>
                                <p>发布操作将会：</p>
                                <ul>
                                    <li>将当前计划状态更新为"已发布"</li>
                                    <li>根据对比结果生成或替换执行计划记录</li>
                                    <li>新增航班将创建新的执行记录</li>
                                    <li>变更航班将替换现有执行记录</li>
                                    <li>取消航班将标记相应的执行记录为已取消</li>
                                </ul>
                                <p>请仔细检查对比结果并根据需要调整匹配关系后再确认发布。</p>
                            </div>
                        }
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                </div>

                <div className="compare-container">
                    <FlightCompare
                        ref={compareRef}
                        scheduleData={{ schedule, flights: schedule?.flights || [] }}
                        onMatchingsChange={handleMatchingsChange}
                        onCompareResult={handleCompareResult}
                        manualMatchings={manualMatchings}
                        loading={loading}
                        mode="publish"
                    />
                </div>
            </div>
        </Drawer>
    );
};

export default SchedulePublishDrawer;
